import { useState, useRef, useEffect } from 'react';
import { Loader2, Send, User, Bot, Brain, Sparkles, MessageCircle } from 'lucide-react';
import exploreApi from '../../exploreApi';

export default function DataChat({ filePath, sourceId, objectPath, sheetName, tablePreview }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextData, setContextData] = useState(null);
  const [loadingContext, setLoadingContext] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const loadContext = async () => {
      setLoadingContext(true);
      const context = {};
      if (tablePreview) {
        context.schema = { columns: tablePreview.columns, row_count: tablePreview.row_count };
        context.sample_data = tablePreview.sample_data?.slice(0, 10);
      }
      try {
        const r = await exploreApi.getSavedAnalyses({ file_path: filePath, source_id: sourceId, object_path: objectPath });
        if (r.success && r.analyses?.length > 0) context.ai_analysis = r.analyses[0].analysis_data;
      } catch { /* silent */ }
      try {
        const r = await exploreApi.getSavedDescriptions({ file_path: filePath, source_id: sourceId, object_path: objectPath, sheet_name: sheetName });
        if (r.success && r.data) context.descriptions = r.data;
      } catch { /* silent */ }
      setContextData(context);
      setLoadingContext(false);

      const intro = {
        id: 'intro',
        role: 'assistant',
        content: `Hi! I'm ready to help you explore **${objectPath || filePath || 'this data'}**. Ask me anything about the data — trends, anomalies, specific columns, or business insights.`,
        timestamp: new Date(),
        followUpQuestions: [
          'What are the key columns in this dataset?',
          'Are there any data quality issues I should know about?',
          'What business insights can you derive from this data?',
          'What are the main trends or patterns in this data?',
        ],
      };
      setMessages([intro]);
    };
    loadContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, sourceId, objectPath, sheetName]);

  const sendMessage = async (text) => {
    const question = text || inputValue.trim();
    if (!question) return;
    setInputValue('');
    setError(null);

    const userMsg = { id: Date.now().toString(), role: 'user', content: question, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const response = await exploreApi.chat({
        user_question: question,
        file_path: filePath,
        source_id: sourceId,
        object_path: objectPath,
        sheet_name: sheetName,
        previous_analysis: contextData,
      });

      if (response.success && response.data) {
        const assistantMsg = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.data.response || response.data.answer || '',
          timestamp: new Date(),
          usage: response.llm_usage,
          followUpQuestions: response.data.follow_up_questions || [],
        };
        setMessages(prev => [...prev, assistantMsg]);
      } else {
        setError(response.error || 'Failed to get response');
      }
    } catch (err) {
      setError(err.message || 'Chat failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (loadingContext) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-3" />
        <span className="text-sm text-gray-500">Loading context…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px]">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50 rounded-t-lg">
        <Brain className="h-5 w-5 text-primary" />
        <div>
          <p className="text-sm font-semibold text-gray-900">AI Data Chat</p>
          <p className="text-xs text-gray-500">Ask anything about {objectPath || filePath || 'this data'}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map(msg => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-primary' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
              {msg.role === 'user' ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-white" />}
            </div>
            <div className={`max-w-[80%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
              <div className={`rounded-xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
              {msg.followUpQuestions?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {msg.followUpQuestions.slice(0, 3).map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)} className="text-xs px-3 py-1.5 border border-primary/30 text-primary rounded-full hover:bg-primary/5 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {msg.usage && (
                <p className="text-[10px] text-gray-400">{msg.usage.model} · {msg.usage.total_tokens} tokens</p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about this data…"
              disabled={loading}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50"
            />
          </div>
          <button
            onClick={() => sendMessage()}
            disabled={loading || !inputValue.trim()}
            className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">
          <Sparkles className="h-3 w-3 inline mr-1" />
          AI-powered · Answers based on schema, sample data, and saved analysis
        </p>
      </div>
    </div>
  );
}
